import React, { useMemo, useState } from "react";
import { Eye, PlusCircle } from "lucide-react";
import {
  DataTable,
  DataTableCard,
  Pagination2,
  EmployeeCell,
  UtilizationCell,
  IconActionButton,
} from "../common/Table2";
import { SimpleTooltip, SearchInput, TableSkeleton } from "../common";
import { ColumnSelector } from "../common/Table/ColumnSelector";
import type { TableColumn } from "../common/Table";
import { capitalizeWords } from "../../utils/nameUtils";
import type { ResourceRecord } from "./ResourceTableSection";

export interface ResourceAllocationTableProps {
  resources: ResourceRecord[];
  loading: boolean;
  total: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  onView?: (resource: ResourceRecord) => void;
  onAssign?: (resource: ResourceRecord) => void;

  search: string;
  onSearchChange: (value: string) => void;
  resourceIds: string[];
  onResourceChange: (ids: string[]) => void;
  isAllocated: string;
  onAllocatedChange: (value: string) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  resourcesList: Array<{ label: string; value: string }>;
}

const OPTIONAL_KEYS = ["designation", "available"];

const ResourceAllocationTable: React.FC<ResourceAllocationTableProps> = ({
  resources,
  loading,
  total,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onView,
  onAssign,
  search,
  onSearchChange,
  resourceIds,
  onResourceChange,
  isAllocated,
  onAllocatedChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  resourcesList,
}) => {
  const [visibleKeys, setVisibleKeys] = useState<string[]>(OPTIONAL_KEYS);

  const toggleVisibility = (key: string) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const columns = useMemo<TableColumn<ResourceRecord>[]>(() => {
    const subtle = "text-sm text-[var(--color-text-secondary)]";

    const renderProjects = (_: unknown, r: ResourceRecord) => {
      const projects = r.projects || [];

      if (projects.length === 0) {
        return (
          <span className="text-[13px] text-[var(--color-text-tertiary)] inline-block">
            No projects
          </span>
        );
      }

      const tooltipContent = (
        <div className="p-2">
          <div className="mb-2 pb-2 border-b border-[var(--color-border-tertiary)]">
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">
              Project & Allocation%
            </span>
          </div>
          <ul className="space-y-1">
            {projects.map((p, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 bg-[var(--color-primary-500)] rounded-full" />
                <span className="text-[var(--color-text-primary)]">{p.projectName}</span>
                <span className="ml-auto text-[var(--color-text-secondary)]">
                  {p.allocationPercentage}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      );

      if (projects.length === 1) {
        return (
          <SimpleTooltip side="top" label={tooltipContent}>
            <div>
              <span className="bg-[var(--color-bg-muted)] text-[var(--color-text-primary)] text-[12px] rounded-full py-1 px-3 cursor-pointer inline-block">
                {projects[0].projectName}
              </span>
            </div>
          </SimpleTooltip>
        );
      }

      const remainingCount = projects.length - 1;
      return (
        <div className="space-y-1">
          <SimpleTooltip side="top" label={tooltipContent}>
            <div>
              <span className="bg-[var(--color-bg-muted)] text-[var(--color-text-primary)] text-[12px] font-medium rounded-full py-1 px-3 cursor-pointer inline-block">
                {projects[0].projectName}
              </span>
            </div>
          </SimpleTooltip>
          <SimpleTooltip side="top" label={tooltipContent}>
            <div>
              <span className="text-[var(--color-primary-700)] bg-[var(--color-primary-100)] text-[12px] rounded-full py-1 px-3 cursor-pointer inline-block">
                View {remainingCount} more
              </span>
            </div>
          </SimpleTooltip>
        </div>
      );
    };

    const renderAllocated = (_: unknown, r: ResourceRecord) => {
      const pct = r.allocatedPercentage ?? 0;
      if (pct > 100) {
        return (
          <SimpleTooltip
            tooltipClassName="-ml-10"
            delay={150}
            side="top"
            label={
              <span className="text-[var(--color-error-dark)]">
                This resource is over-allocated
              </span>
            }
          >
            <span>
              <UtilizationCell pct={pct} color="var(--color-error-dark)" />
            </span>
          </SimpleTooltip>
        );
      }
      return <UtilizationCell pct={pct} />;
    };

    return [
      {
        key: "resource",
        title: "Resource",
        label: "Resource",
        required: true,
        truncate: false,
        render: (_: unknown, r: ResourceRecord) => (
          <EmployeeCell
            name={`${capitalizeWords(r.firstName)} ${capitalizeWords(r.lastName) || ""}`.trim()}
            sub={r.designation}
          />
        ),
      },
      {
        key: "designation",
        title: "Designation",
        label: "Designation",
        dataIndex: "designation",
        render: (value: any) => (
          <span className={value ? "text-sm text-[var(--color-text-primary)]" : subtle}>
            {value || "N/A"}
          </span>
        ),
      },
      {
        key: "allocated",
        title: "Allocated %",
        label: "Allocated %",
        required: true,
        render: renderAllocated,
      },
      {
        key: "available",
        title: "Available %",
        label: "Available %",
        render: (_: unknown, r: ResourceRecord) => (
          <span className="text-sm text-[var(--color-text-primary)]">
            {r.availablePercentage ?? 0}%
          </span>
        ),
      },
      {
        key: "projects",
        title: "Projects",
        label: "Projects",
        required: true,
        truncate: false,
        render: renderProjects,
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        truncate: false,
        align: "right",
        render: (_: unknown, r: ResourceRecord) => (
          <div className="flex items-center justify-end gap-1">
            {onView && (
              <SimpleTooltip label="View" side="top" delay={300}>
                <div>
                  <IconActionButton onClick={() => onView(r)}>
                    <Eye className="w-4 h-4" />
                  </IconActionButton>
                </div>
              </SimpleTooltip>
            )}
            {onAssign && (
              <SimpleTooltip label="Assign" side="top" delay={300}>
                <div>
                  <IconActionButton onClick={() => onAssign(r)}>
                    <PlusCircle className="w-4 h-4" />
                  </IconActionButton>
                </div>
              </SimpleTooltip>
            )}
          </div>
        ),
      },
    ];
  }, [onView, onAssign]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.required || visibleKeys.includes(c.key)),
    [columns, visibleKeys],
  );

  return (
    <DataTableCard
      title="Resources"
      count={total}
      toolbar={
        <>
          <SearchInput
            placeholder="Search project / client..."
            value={search}
            onChange={onSearchChange}
            className="w-full sm:w-[220px]"
          />
          <ColumnSelector
            columns={columns}
            visibleKeys={visibleKeys}
            onToggle={toggleVisibility}
          />
        </>
      }
      pagination={
        <Pagination2
          total={total}
          page={currentPage}
          pageSize={itemsPerPage}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          onPageChange={onPageChange}
          onPageSizeChange={onItemsPerPageChange}
        />
      }
    >
      <DataTable
        columns={visibleColumns}
        rows={resources}
        rowKey={(r) => r._id}
        loading={loading}
        skeleton={<TableSkeleton rows={itemsPerPage} columns={visibleColumns.length} />}
        emptyMessage="No resources found"
        responsive="scroll"
      />
    </DataTableCard>
  );
};

export default ResourceAllocationTable;